import React, { useEffect, useState } from "react";
import "../styles/vendors.css";
import {
  Plus, Search, Building2, Phone, Mail, MapPin,
  Edit, Trash2, Filter, AlertCircle,
  Hash, CreditCard, Globe, FileText, User,
} from "lucide-react";
import CreateVendorDialog from "../components/vendors/CreateVendorDialog";
import axios from "axios";

const BASE_URL = "https://crmapi.zauntrip.com";

const TYPE_COLORS = {
  "Online Portal":          { bg: "#ede9fe", color: "#5b21b6" },
  "Hotel":                  { bg: "#dbeafe", color: "#1e40af" },
  "DMC":                    { bg: "#d1fae5", color: "#065f46" },
  "Transporter":            { bg: "#fef3c7", color: "#92400e" },
  "Airlines":               { bg: "#e0f2fe", color: "#075985" },
  "Visa Service Provider":  { bg: "#fce7f3", color: "#9d174d" },
  "Travel Insurance":       { bg: "#f0fdf4", color: "#166534" },
  "Cruise":                 { bg: "#cffafe", color: "#164e63" },
  "Sightseeing":            { bg: "#fef9c3", color: "#854d0e" },
  "Other":                  { bg: "#f3f4f6", color: "#374151" },
};

export default function Vendors() {
  const [vendors, setVendors]             = useState([]);
  const [showCreateDialog, setShowCreate] = useState(false);
  const [showEditDialog, setShowEdit]     = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [searchTerm, setSearchTerm]       = useState("");
  const [filterType, setFilterType]       = useState("all");
  const [filterActive, setFilterActive]   = useState("all");
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => { fetchVendors(); }, []);

  const fetchVendors = async () => {
    try {
      setLoading(true); setError(null);
      const res = await axios.get(`${BASE_URL}/api/vendors`);
      if (res.data.success) setVendors(res.data.data || []);
      else throw new Error(res.data.message);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load vendors");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVendor = async (data) => {
    try {
      setActionLoading(true);
      const res = await axios.post(`${BASE_URL}/api/vendors`, data);
      if (res.data.success) { setShowCreate(false); fetchVendors(); }
      else throw new Error(res.data.message);
    } catch (err) {
      alert(`Error: ${err.response?.data?.message || err.message}`);
      return Promise.reject(err);
    } finally { setActionLoading(false); }
  };

  const handleUpdateVendor = async (data) => {
    try {
      setActionLoading(true);
      const res = await axios.put(`${BASE_URL}/api/vendors/${selectedVendor._id}`, data);
      if (res.data.success) { setShowEdit(false); setSelectedVendor(null); fetchVendors(); }
      else throw new Error(res.data.message);
    } catch (err) {
      alert(`Error: ${err.response?.data?.message || err.message}`);
      return Promise.reject(err);
    } finally { setActionLoading(false); }
  };

  const handleToggleActive = async (vendor) => {
    const action   = vendor.isActive ? "deactivate" : "activate";
    const endpoint = vendor.isActive
      ? `${BASE_URL}/api/vendors/${vendor._id}`
      : `${BASE_URL}/api/vendors/${vendor._id}/activate`;

    if (!window.confirm(`Are you sure you want to ${action} this vendor?`)) return;
    try {
      setActionLoading(true);
      const res = vendor.isActive
        ? await axios.delete(endpoint)
        : await axios.patch(endpoint);
      if (res.data.success) fetchVendors();
      else throw new Error(res.data.message);
    } catch (err) {
      alert(`Error: ${err.response?.data?.message || err.message}`);
    } finally { setActionLoading(false); }
  };

  /* ── filter logic ── */
  const filtered = vendors.filter((v) => {
    const text = `${v.vendorLegalName || ""} ${v.contactPersonName || ""} ${v.vendorId || ""}`.toLowerCase();
    const matchSearch =
      text.includes(searchTerm.toLowerCase()) ||
      v.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.contactNumber?.includes(searchTerm) ||
      (v.serviceLocations || []).some((l) => l.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchType   = filterType === "all"   || v.vendorType === filterType;
    const matchStatus =
      filterActive === "all" ||
      (filterActive === "active"   && v.isActive) ||
      (filterActive === "inactive" && !v.isActive);

    return matchSearch && matchType && matchStatus;
  });

  const getAddress = (v) => {
    if (!v.address) return null;
    return [v.address.addressLine1, v.address.city, v.address.stateProvince, v.address.pinCode, v.address.country]
      .filter(Boolean).join(", ");
  };

  const typeStyle = (type) => TYPE_COLORS[type] || TYPE_COLORS["Other"];

  /* ── unique vendor types for filter dropdown ── */
  const vendorTypes = [...new Set(vendors.map((v) => v.vendorType).filter(Boolean))];

  return (
    <div className="vendors-page">

      {/* ── Header ── */}
      <div className="vendors-header">
        <div>
          <h1>Vendors</h1>
          <p>Manage travel vendors, hotels, DMCs and service providers</p>
        </div>
        <button className="btn-primary" onClick={() => setShowCreate(true)} disabled={actionLoading}>
          <Plus size={16} /> Add Vendor
        </button>
      </div>

      {/* ── Filters ── */}
      <div className="vendors-filters">
        <div className="search-box">
          <Search size={16} />
          <input
            placeholder="Search by name, email, vendor ID, location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <Filter size={16} />
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All Types</option>
            {vendorTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <Filter size={16} />
          <select value={filterActive} onChange={(e) => setFilterActive(e.target.value)}>
            <option value="all">All Status</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="vendors-stats">
        <div className="stat-card"><h3>{vendors.filter((v) => v.isActive).length}</h3><p>Active Vendors</p></div>
        <div className="stat-card"><h3>{vendors.filter((v) => v.vendorType === "Hotel").length}</h3><p>Hotels</p></div>
        <div className="stat-card"><h3>{vendors.filter((v) => v.vendorType === "DMC").length}</h3><p>DMCs</p></div>
        <div className="stat-card"><h3>{vendors.length}</h3><p>Total Vendors</p></div>
      </div>

      {/* ── Loading / Error ── */}
      {loading && <div className="loading-state"><p>Loading vendors...</p></div>}

      {error && !loading && (
        <div className="error-state">
          <AlertCircle size={24} />
          <p>{error}</p>
          <button onClick={fetchVendors} className="btn-outline" disabled={actionLoading}>Retry</button>
        </div>
      )}

      {/* ── Grid ── */}
      {!loading && !error && (
        <div className="vendors-grid">
          {filtered.map((vendor) => (
            <div key={vendor._id} className="vendor-card">

              {/* Card header */}
              <div className="vendor-card-header">
                <div className="vendor-avatar">
                  <Building2 size={22} />
                </div>
                <div className="vendor-info">
                  <h3>{vendor.vendorLegalName || "—"}</h3>
                  <div className="vendor-meta">
                    {vendor.vendorType && (
                      <span
                        className="badge"
                        style={{ background: typeStyle(vendor.vendorType).bg, color: typeStyle(vendor.vendorType).color }}
                      >
                        {vendor.vendorType}
                      </span>
                    )}
                    {vendor.vendorId && <span className="badge badge-primary">{vendor.vendorId}</span>}
                    {!vendor.isActive && <span className="badge badge-danger">Inactive</span>}
                  </div>
                </div>
              </div>

              {/* Card body */}
              <div className="vendor-details">

                {/* Contact */}
                <div className="detail-group">
                  {vendor.contactPersonName && <p><User size={13} /> {vendor.contactPersonName}{vendor.designation ? ` · ${vendor.designation}` : ""}</p>}
                  {vendor.email            && <p><Mail size={13} /><a href={`mailto:${vendor.email}`}>{vendor.email}</a></p>}
                  {vendor.contactNumber    && <p><Phone size={13} /><a href={`tel:${vendor.contactNumber}`}>{vendor.contactNumber}</a></p>}
                  {vendor.secondaryContactNumber && vendor.secondaryContactNumber !== vendor.contactNumber && (
                    <p><Phone size={13} /> Alt: {vendor.secondaryContactNumber}</p>
                  )}
                  {getAddress(vendor) && <p className="address"><MapPin size={13} /><span>{getAddress(vendor)}</span></p>}
                  {vendor.serviceLocations?.length > 0 && (
                    <p><Globe size={13} /><span style={{ flex: 1 }}>{vendor.serviceLocations.join(", ")}</span></p>
                  )}
                </div>

                {/* Legal */}
                {(vendor.gstNumber || vendor.panNumber || vendor.msmeRegistrationNumber) && (
                  <div className="detail-group">
                    {vendor.businessType            && <p><FileText size={13} /> {vendor.businessType}</p>}
                    {vendor.gstNumber               && <p><Hash size={13} /> GST: {vendor.gstNumber}</p>}
                    {vendor.panNumber               && <p><Hash size={13} /> PAN: {vendor.panNumber}</p>}
                    {vendor.msmeRegistrationNumber  && <p><Hash size={13} /> MSME: {vendor.msmeRegistrationNumber}</p>}
                  </div>
                )}

                {/* Bank */}
                {vendor.bankName && (
                  <div className="detail-group">
                    <p><CreditCard size={13} /> {vendor.bankName}{vendor.branchName ? ` — ${vendor.branchName}` : ""}</p>
                    {vendor.accountNumber && (
                      <p style={{ fontSize: 12, color: "#6b7280", paddingLeft: 21 }}>A/C: ••••{vendor.accountNumber.slice(-4)}</p>
                    )}
                  </div>
                )}

              </div>

              {/* Actions */}
              <div className="vendor-actions">
                <button
                  className="btn-outline btn-sm"
                  onClick={() => { setSelectedVendor(vendor); setShowEdit(true); }}
                  disabled={actionLoading}
                >
                  <Edit size={13} /> Edit
                </button>
                <button
                  className={vendor.isActive ? "btn-danger btn-sm" : "btn-success btn-sm"}
                  onClick={() => handleToggleActive(vendor)}
                  disabled={actionLoading}
                >
                  <Trash2 size={13} /> {vendor.isActive ? "Deactivate" : "Activate"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="empty-state">
          <Building2 size={36} />
          <h3>No vendors found</h3>
          <p>{searchTerm ? "Try a different search term" : "Add your first vendor to get started"}</p>
          {!searchTerm && (
            <button className="btn-primary" onClick={() => setShowCreate(true)} disabled={actionLoading}>
              Add Vendor
            </button>
          )}
        </div>
      )}

      {/* ── Dialogs ── */}
      <CreateVendorDialog
        open={showCreateDialog}
        onClose={() => setShowCreate(false)}
        onSubmit={handleCreateVendor}
        isLoading={actionLoading}
      />

      {selectedVendor && (
        <CreateVendorDialog
          open={showEditDialog}
          onClose={() => { setShowEdit(false); setSelectedVendor(null); }}
          onSubmit={handleUpdateVendor}
          initialData={selectedVendor}
          isEdit={true}
          isLoading={actionLoading}
        />
      )}
    </div>
  );
}